import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Search, CheckCircle2, X } from "lucide-react";
import useRequest from "../../../components/hook/use-req";
import { TeamDetails } from "../../../components/crm/customer/types";
import { LeadRecord } from "./shared";

interface AssignAgentModalProps {
  lead: LeadRecord | null;
  teamMembers: TeamDetails[];
  onClose: () => void;
  onAssign: (lead: LeadRecord, member: TeamDetails | null) => void;
}

const AgentRow: React.FC<{ label: string; selected: boolean; onClick: () => void }> = ({ label, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm text-left border-none cursor-pointer transition-colors rounded-lg ${
      selected
        ? "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 font-medium"
        : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
    }`}
  >
    <span className="truncate">{label}</span>
    {selected && <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />}
  </button>
);

export const AssignAgentModal: React.FC<AssignAgentModalProps> = ({ lead, teamMembers, onClose, onAssign }) => {
  const token = localStorage.getItem("authToken");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selfMember, setSelfMember] = useState<TeamDetails | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { makeRequest: getUserData } = useRequest("/auth/get_userdata", "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    if (!lead) return;
    setSearch("");
    setSelectedId(lead.assignedTo);
  }, [lead]);

  // "Assign to self" — resolved against the team list by the logged-in
  // user's id/email from GET /auth/get_userdata, never guessed. Hidden
  // entirely if it can't be confirmed.
  useEffect(() => {
    if (!lead || teamMembers.length === 0) return;
    let cancelled = false;
    (async () => {
      const [response] = await getUserData();
      if (cancelled) return;
      const userId = response?.payload?.user_id;
      const email = response?.payload?.email;
      const match = teamMembers.find((m) => m.id === userId || (email && m.email === email)) || null;
      setSelfMember(match);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, teamMembers]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return teamMembers;
    return teamMembers.filter((m) => `${m.first_name} ${m.last_name}`.toLowerCase().includes(q));
  }, [teamMembers, search]);

  const selectedLabel = useMemo(() => {
    if (!selectedId) return "Select an Agent";
    const m = teamMembers.find((x) => x.id === selectedId);
    return m ? `${m.first_name} ${m.last_name}`.trim() : "Select an Agent";
  }, [selectedId, teamMembers]);

  if (!lead) return null;

  const handleAssignClick = async () => {
    if (!selectedId) return;
    const member = teamMembers.find((m) => m.id === selectedId) || null;
    if (!member) {
      toast.error("Could not determine the selected agent.");
      return;
    }
    setSubmitting(true);
    await onAssign(lead, member);
    setSubmitting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">Assign Agent</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent border-none cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>
        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-4">Choose an agent for this lead to continue the conversation</p>

        <div className="flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 mb-3">
          <span className={`text-sm truncate ${selectedId ? "text-gray-900 dark:text-gray-100 font-medium" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}`}>
            {selectedLabel}
          </span>
          <ChevronDown size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" />
        </div>

        <div className="relative mb-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Agents"
            className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm outline-none focus:border-indigo-400"
          />
        </div>

        <div className="max-h-56 overflow-y-auto mb-4">
          {selfMember && (
            <AgentRow
              label="Assign to self"
              selected={selectedId === selfMember.id}
              onClick={() => setSelectedId(selfMember.id)}
            />
          )}
          {filteredMembers.length === 0 ? (
            <p className="px-3 py-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">No agents found</p>
          ) : (
            filteredMembers.map((m) => (
              <AgentRow
                key={m.id}
                label={`${m.first_name} ${m.last_name}`.trim()}
                selected={selectedId === m.id}
                onClick={() => setSelectedId(m.id)}
              />
            ))
          )}
        </div>

        <button
          onClick={handleAssignClick}
          disabled={!selectedId || submitting}
          className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-2"
        >
          {submitting ? "Assigning…" : "Assign Lead"}
        </button>
        <button
          onClick={onClose}
          className="w-full py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AssignAgentModal;
